import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Badge, Spinner, Alert, Nav } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import { apiGet, errorText, usd, date, PAYOUT_STATUS_VARIANT } from "./sellerApi";

/**
 * Seller portal: what each sale earns, what is on hold and why, and a ledger
 * of every sale, fee, deduction, refund and payout with a running balance.
 * Sellers work in US dollars, so every amount here is in dollars.
 */
export default function SellerPayoutsPage() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("items");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await apiGet("/store-seller/payouts");
                setData(res.data?.data);
            } catch (e) {
                setError(errorText(e, "Could not load your payouts."));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const s = data?.summary || {};

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">My Payouts &amp; Ledger</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="/dashboard" className="mc-breadcrumb-link">Dashboard</Link></li>
                                <li className="mc-breadcrumb-item">Payouts</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {error && <Col xl={12}><Alert variant="danger">{error}</Alert></Col>}
                {loading ? (
                    <Col xl={12}><div className="text-center py-5"><Spinner animation="border" /></div></Col>
                ) : data && (
                    <>
                        <Col xl={12}>
                            <Row className="g-3 mb-3">
                                {[
                                    ["Gross sales", s.gross_sales_usd, ""],
                                    [s.storage_preference === "admin_store" ? `Commission (${s.fee_percent}%)` : `Platform fee (${s.fee_percent}%)`, s.fees_usd, ""],
                                    ["Net earned", s.net_earned_usd, ""],
                                    ["On hold", Number(s.on_hold_usd || 0) + Number(s.complaint_hold_usd || 0), "text-warning"],
                                    ["Ready / being paid", Number(s.eligible_usd || 0) + Number(s.approved_usd || 0), "text-primary"],
                                    ["Paid to you", s.paid_usd, "text-success"],
                                ].map(([label, v, cls]) => (
                                    <Col key={label} xs={6} md={4} xl={2}>
                                        <div className="mc-card p-3 bg-white rounded-3 shadow-sm border h-100">
                                            <div className="text-muted small">{label}</div>
                                            <div className={`fw-bold fs-5 ${cls}`}>{usd(v)}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            <Alert variant="light" className="border small">
                                Payments are held for {s.hold_days} days after each sale. The first {s.complaint_window_days} days are the complaint
                                window; if a buyer complains, the payment stays on hold until the complaint is resolved. After that Watches Pool
                                transfers your net amount to your default <Link to="/seller-payment-methods">payment method</Link>.
                                {s.next_release_date ? ` Next release: ${date(s.next_release_date)}.` : ""}
                            </Alert>
                        </Col>

                        <Col xl={12}>
                            <Card className="border-0 shadow-sm rounded-4">
                                <Card.Body className="p-4">
                                    <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
                                        <Nav.Item><Nav.Link eventKey="items">Sales &amp; holds</Nav.Link></Nav.Item>
                                        <Nav.Item><Nav.Link eventKey="ledger">Ledger</Nav.Link></Nav.Item>
                                    </Nav>

                                    {tab === "items" ? (
                                        data.items.length === 0 ? <p className="text-muted mb-0">No sales yet.</p> : (
                                            <Table responsive hover className="align-middle small mb-0">
                                                <thead>
                                                    <tr><th>Order</th><th>Watch</th><th>Sold</th><th>Gross ($)</th><th>Fee ($)</th><th>Deductions ($)</th><th>Net ($)</th><th>Status</th></tr>
                                                </thead>
                                                <tbody>
                                                    {data.items.map((r) => (
                                                        <tr key={r.id}>
                                                            <td><strong>{r.order_number}</strong><span className="d-block text-muted">{r.buyer_name}</span></td>
                                                            <td>{r.title}<span className="d-block text-muted">{r.product_code}</span></td>
                                                            <td>{date(r.sold_at)}</td>
                                                            <td>{usd(r.gross_usd)}</td>
                                                            <td>{usd(r.fee_usd)}<span className="d-block text-muted">{Number(r.fee_percent)}%</span></td>
                                                            <td>{usd(Number(r.shipping_deduction_usd || 0) + Number(r.other_deductions_usd || 0))}{r.deductions_note && <span className="d-block text-muted">{r.deductions_note}</span>}</td>
                                                            <td className="fw-semibold">{usd(r.net_usd)}</td>
                                                            <td>
                                                                <Badge bg={PAYOUT_STATUS_VARIANT[r.status] || "secondary"}>{r.status_label}</Badge>
                                                                {r.hold_reason && <span className="d-block text-muted">{r.hold_reason}</span>}
                                                                {r.status === "paid" && <span className="d-block text-muted">{date(r.paid_at)} · Ref {r.transfer_reference}{r.payment_method ? ` · ${r.payment_method}` : ""}</span>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )
                                    ) : (
                                        <>
                                            <p className="mb-2">Balance Watches Pool owes you: <strong>{usd(data.ledger.balance_usd)}</strong></p>
                                            <Table responsive className="align-middle small mb-0">
                                                <thead>
                                                    <tr><th>Date</th><th>Description</th><th className="text-end">Credit</th><th className="text-end">Debit</th><th className="text-end">Balance</th><th>Status</th></tr>
                                                </thead>
                                                <tbody>
                                                    {data.ledger.entries.map((e, i) => (
                                                        <tr key={`${e.type}-${e.settlement_id || e.payment_id || i}-${i}`}>
                                                            <td className="text-nowrap">{date(e.date)}</td>
                                                            <td>{e.description}</td>
                                                            <td className="text-end text-success">{e.credit_usd !== null && e.credit_usd !== undefined ? usd(e.credit_usd) : ""}</td>
                                                            <td className="text-end text-danger">{e.debit_usd !== null && e.debit_usd !== undefined ? usd(e.debit_usd) : ""}</td>
                                                            <td className="text-end fw-semibold">{usd(e.balance_usd)}</td>
                                                            <td>{e.status_label && <Badge bg={PAYOUT_STATUS_VARIANT[e.status] || "secondary"}>{e.status_label}</Badge>}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>
        </PageLayout>
    );
}
